import { format } from 'date-fns';
import { match } from 'ts-pattern';

import { AddRankSuffix } from '~/components/utils/formatters';
import { getTeamOrPlayerImageUrl } from '~/domains/common/utils/getTeamOrPlayerImageUrl';

import type {
  PublicBaseballGame,
  PublicGame,
  PublicGameWithTeams,
  APIUserProp,
  SoccerState,
} from '../../services/interfaces/propsService/api';
import { getLeagueFormat, isSoccerLeague } from '../../services/interfaces/propsService/api/leagues';
import { getRoundFromMeta } from '../../services/interfaces/propsService/api/propMeta';
import type { TGroupSlip } from '../../services/interfaces/propsService/parsed/slips';
import type { TUserProp } from '../../services/interfaces/propsService/parsed/props';

import { getEventStatus, parsePropStatus } from './parsePropStatus';
import { formatPlayerTeamFirstGameDescription, playerTeamIdForPick } from './playerTeamId.utils';
import { parseGameLog, formatThruScore } from './golfUtils';

export type GenericGameState = {
  phase: string;
  inning?: number;
  outs?: number;
};

export function parseLiveGameTime(state: PublicGame['state']): GenericGameState {
  if (state?.league === 'pga') {
    return { phase: '' };
  }
  if (state?.league === 'mlb') {
    if (state.current_inning_phase === 'top' && state.outs === 3) {
      return {
        phase: 'Mid',
        inning: state.current_inning,
        outs: undefined,
      };
    }
    if (state.current_inning_phase === 'bottom' && state.outs === 3) {
      return {
        phase: 'End',
        inning: state.current_inning,
        outs: undefined,
      };
    }
    if (state.current_inning_phase === 'bottom') {
      return {
        phase: 'Bot',
        inning: state.current_inning,
        outs: state.outs,
      };
    }
    return {
      phase: 'Top',
      inning: state.current_inning,
      outs: state.outs,
    };
  }

  if (state && 'phase' in state && state.phase === 'overtime') {
    return { phase: `OT - ${(state as { clock?: string }).clock}` };
  }

  if (state && 'phase' in state && state.phase === 'pregame') {
    return { phase: (state as { clock?: string }).clock ?? '' };
  }

  if (state?.league === 'nhl') {
    const nhlState = state as { period?: number; phase?: string; clock?: string };
    if (nhlState.phase === 'shootout') {
      return { phase: 'SO' };
    }
    return {
      phase: `${AddRankSuffix(nhlState.period ?? 0)} ${nhlState?.clock ?? ''}`,
    };
  }

  if (state?.league === 'ncaab') {
    const ncaabState = state as { half?: number; clock?: string };
    return {
      phase: `${AddRankSuffix(ncaabState.half ?? 1)} Half ${ncaabState?.clock ?? ''}`,
    };
  }

  const soccerState = state as SoccerState;
  if (soccerState?.sport === 'soccer' || isSoccerLeague(state?.league)) {
    return { phase: getSoccerPhase(soccerState) };
  }

  const qtrState = state as { quarter?: number; clock?: string };
  return {
    phase: `${AddRankSuffix(qtrState.quarter ?? 1)} QTR ${qtrState?.clock ?? ''}`,
  };
}

function getSoccerPhase(state: SoccerState): string {
  const { clock }: SoccerState = state;
  const hasPenaltyScore =
    typeof state.home_penalties === 'number' && typeof state.away_penalties === 'number';
  const penaltyScore = hasPenaltyScore
    ? `PEN ${state.home_penalties}-${state.away_penalties}`
    : 'PEN';

  return match(state.match_status)
    .with('1st_half', () => `1st Half ${clock ?? ''}`.trimEnd())
    .with('2nd_half', () => `2nd Half ${clock ?? ''}`.trimEnd())
    .with('1st_extra', () => `1st ET ${clock ?? ''}`.trimEnd())
    .with('2nd_extra', () => `2nd ET ${clock ?? ''}`.trimEnd())
    .with('halftime', () => 'HT')
    .with('extra_time_halftime', () => 'ET HT')
    .with('interrupted', () => 'INT')
    .with('penalties', () => penaltyScore)
    .with('ended', () => (hasPenaltyScore ? penaltyScore : 'FT'))
    .with('not_started', () => '')
    .otherwise(() => ''); // patch for type mismatch until fully tested
}

export function getPlayerLineup(
  homeLineup: PublicBaseballGame['home']['lineup'],
  awayLineup: PublicBaseballGame['away']['lineup'],
  entityId: string
) {
  const home = homeLineup?.find((lineup) => lineup.id === entityId && lineup.inning === 0);
  const away = awayLineup?.find((lineup) => lineup.id === entityId && lineup.inning === 0);
  return home ?? away;
}

type SlipPropLike = TUserProp | APIUserProp;

function parseBaseSlipProps(prop: SlipPropLike) {
  const round = getRoundFromMeta(prop.meta);
  const eventStatus = getEventStatus(prop);
  const gameLog = parseGameLog(prop.game_log, round);
  const lineDisplay = prop.line_display?.trim();

  const getGameTime = (): GenericGameState | undefined => {
    if (eventStatus !== 'IN_PROGRESS') return undefined;

    const thruScoreLabel = formatThruScore(gameLog.thru, gameLog.golfScore);
    if (thruScoreLabel) return { phase: thruScoreLabel };

    return parseLiveGameTime(prop.game.state);
  };

  return {
    id: prop.id,
    name: prop.entity_name,
    playerLastName: prop.entity.player_details.last_name,
    status: parsePropStatus(prop),
    line: {
      value: prop.line,
      label: prop.type_display,
      type: prop.type,
      ...(lineDisplay ? { display: lineDisplay } : {}),
    },
    gameId: prop.game.id,
    playerId: prop.entity.id,
    playerName: prop.entity_name,
    playerImage: getTeamOrPlayerImageUrl(prop.league, prop.entity_id),
    gameStatus: eventStatus,
    direction: prop.direction,
    score: prop.score,
    round,
    gameLog,
    ...(eventStatus === 'IN_PROGRESS' ? { gameTime: getGameTime() } : {}),
    startDate: `${format(prop.game.start_date, 'EEE, LLL d')} • ${format(
      prop.game.start_date,
      'h:mm a'
    )}`,
    alternate: prop.alternate ?? false,
    replaced: prop.replaced ?? false,
    used_as_alternate: prop.used_as_alternate ?? false,
  };
}

function parseTeamSlipProps(prop: SlipPropLike, game: PublicGameWithTeams) {
  const playerTeamId = playerTeamIdForPick(prop as TUserProp, game);

  return {
    position: prop.entity.player_details.position,
    playerTeamId,
    awayTeam: {
      id: game.away.id,
      alias: game.away.alias,
      points: game.away.points ?? game.away.runs,
      lineup: game.away.lineup,
    },
    homeTeam: {
      id: game.home.id,
      alias: game.home.alias,
      points: game.home.points ?? game.home.runs,
      lineup: game.home.lineup,
    },
    gameDescription: formatPlayerTeamFirstGameDescription({
      homeTeam: game.home,
      awayTeam: game.away,
      playerTeamId,
      league: prop.game.league,
    }),
    ...(prop.game.league === 'mlb'
      ? {
          playerLineup: getPlayerLineup(game.home.lineup, game.away.lineup, prop.entity.id),
        }
      : {}),
  };
}

function parseTournamentSlipProps(prop: SlipPropLike) {
  const gameName = prop.game.name;
  const round = getRoundFromMeta(prop.meta);

  return {
    playerTeamId: prop.entity_id,
    gameDescription: [gameName, round != null ? `R${round}` : null].filter(Boolean).join(' • '),
    awayTeam: undefined,
    homeTeam: undefined,
  };
}

function parseSlipProp(prop: SlipPropLike) {
  const base = parseBaseSlipProps(prop);
  const gameProps =
    getLeagueFormat(prop.league) === 'team'
      ? parseTeamSlipProps(prop, prop.game as PublicGameWithTeams)
      : parseTournamentSlipProps(prop);

  return { ...base, ...gameProps };
}

export type ParsedSlipProp = ReturnType<typeof parseSlipProp>;

export function parseGroupSplipProps(props: TGroupSlip['props']) {
  return props.map(parseSlipProp);
}

export function parseGroupSlip(slip: TGroupSlip, userId: string) {
  return {
    id: slip.id,
    rank: (() => {
      if (slip.status === 'void') {
        return '-';
      }

      return slip.tied ? `T${slip.rank}` : `${slip.rank}`;
    })(),
    props: parseGroupSplipProps(slip.props),
    name: slip.user.handle,
    avatarUrl: slip.user.avatar_url,
    isUserEntry: slip.user.id === userId,
    score: typeof slip.amount_won === 'number' && slip.status !== 'void' ? slip.score : undefined,
  };
}

export function parseLeaderboardProp(prop: SlipPropLike) {
  return parseSlipProp(prop);
}
